import "./style.css";

import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import Utils, { ConsentType } from "../../../utils/utmManager";
import { privacyPolicyLocale } from "./privacyPolicyLocale";
import { CustomButton } from "../../atoms/CustomButton";

const HIDDEN_PATH_PREFIXES = [
  "/loyalty-program/",
  "/event/",
  "/business-card/",
  "/custom-registration/",
  "/sign/",
  "/process/",
  "/login/",
  "/registration-complete",
  "/user/reset-password",
  "/link-unavailable",
];

const HIDDEN_PATH_REGEXES = [/^\/user\/[^/]+\/registration(\/|$)/];

const isHiddenPath = (pathname: string) =>
  HIDDEN_PATH_PREFIXES.some((p) => pathname.startsWith(p)) ||
  HIDDEN_PATH_REGEXES.some((r) => r.test(pathname));

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

type checkBoxesType = {
  necessary: boolean;
  analytics: boolean;
  preferences: boolean;
  marketing: boolean;
};

const ConsentManager = () => {
  // Shown on the client pages too: its texts are in the namespace translated into every client language.
  const { t, i18n } = useTranslation("clientView");
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const hidden = isHiddenPath(pathname);

  const [checkBoxes, setCheckBoxes] = useState<checkBoxesType>({
    necessary: true,
    analytics: true,
    preferences: true,
    marketing: false,
  });

  const handlePersistConsents = useCallback(
    (consent: ConsentType) => {
      if (
        typeof Storage !== "undefined" &&
        window.localStorage &&
        typeof localStorage !== "undefined"
      ) {
        const currentConsent = Utils.readSharedConsent();

        if (!currentConsent) {
          if (typeof window !== "undefined" && window.gtag)
            window.gtag("consent", "update", consent);

          Utils.writeSharedConsent(consent);

          if (searchParams) Utils.utmManager(searchParams);

          document.body.classList.remove("consent-manager-open");

          setOpen(false);
        }
      }
    },
    [searchParams]
  );

  const handleConsents = useCallback(
    (type: "all" | "selection" | "none", checkBoxes: checkBoxesType) => {
      const enumConsent: {
        all: ConsentType;
        selection: ConsentType;
        none: ConsentType;
      } = {
        all: {
          ad_storage: "granted",
          ad_user_data: "granted",
          ad_personalization: "granted",
          analytics_storage: "granted",
          personalization_storage: "granted",
          functionality_storage: "granted",
          security_storage: "granted",
        },
        selection: {
          ad_storage: checkBoxes.marketing ? "granted" : "denied",
          ad_user_data: checkBoxes.marketing ? "granted" : "denied",
          ad_personalization: checkBoxes.marketing ? "granted" : "denied",
          analytics_storage: checkBoxes.analytics ? "granted" : "denied",
          personalization_storage: checkBoxes.preferences
            ? "granted"
            : "denied",
          functionality_storage: "granted",
          security_storage: "granted",
        },
        none: {
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          analytics_storage: "denied",
          personalization_storage: "denied",
          functionality_storage: "granted",
          security_storage: "granted",
        },
      };

      handlePersistConsents(enumConsent[type]);
    },
    [handlePersistConsents]
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const handleReopen = () => {
      setShowDetails(true);
      setOpen(true);
      document.body.classList.add("consent-manager-open");
    };
    window.addEventListener("open-consent-preferences", handleReopen);
    return () =>
      window.removeEventListener("open-consent-preferences", handleReopen);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    let timer: ReturnType<typeof setTimeout> | undefined;

    if (
      typeof Storage !== "undefined" &&
      window.localStorage &&
      typeof localStorage !== "undefined"
    ) {
      const currentConsent = Utils.readSharedConsent();

      if (currentConsent) {
        if (typeof window !== "undefined" && window.gtag)
          window.gtag("consent", "update", currentConsent);
        if (!localStorage.getItem("consentMode"))
          localStorage.setItem("consentMode", JSON.stringify(currentConsent));
      } else if (!hidden) {
        timer = setTimeout(() => {
          setOpen(true);
          document.body.classList.add("consent-manager-open");
        }, 3000);
      }
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (
        typeof Storage !== "undefined" &&
        window.localStorage &&
        typeof localStorage !== "undefined"
      ) {
        document.body.classList.remove("consent-manager-open");
        setOpen(false);
      }
    };
  }, [isMounted, hidden]);

  useEffect(() => {
    if (!open) {
      setVisible(false);
      return;
    }
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  if (hidden || !open) return null;

  return (
    <div className={`consent-manager ${visible ? "open" : ""}`}>
      <div className="flex justify-center mb-2">
        <div className="w-6 h-6 text-blue-400">🍪</div>
      </div>

      <p className="text-center text-gray-300 text-sm font-medium leading-relaxed mb-4">
        <Trans
          t={t}
          i18nKey="consentManager.description"
          components={{
            1: (
              <a
                href={`${
                  process.env.REACT_APP_LANDING_URL || ""
                }/${privacyPolicyLocale(i18n.language)}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline text-sm"
              >
                Privacy Policy
              </a>
            ),
          }}
        />
      </p>
      <div className="flex flex-wrap justify-center gap-2 mb-3">
        <CustomButton
          variant="primary"
          size="md"
          className="text-xs"
          onClick={() => handleConsents("all", checkBoxes)}
          title={t("consentManager.acceptAll")}
        />
        <button
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-xs font-medium transition-colors"
          onClick={() => handleConsents("none", checkBoxes)}
        >
          {t("consentManager.essentialOnly")}
        </button>
        <button
          className="px-4 py-2 border border-gray-600 hover:border-gray-500 rounded-lg text-gray-300 text-xs font-medium transition-colors"
          onClick={() => setShowDetails(!showDetails)}
        >
          {t("consentManager.customize")}
        </button>
      </div>
      {showDetails && (
        <div className="space-y-2 border-t border-gray-700 pt-3">
          <div className="flex items-center justify-between">
            <label className="flex items-center text-xs text-gray-300">
              <input
                type="checkbox"
                checked={checkBoxes.necessary}
                readOnly
                disabled
                className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("consentManager.categories.essential")}
            </label>
            <span className="text-xs text-gray-500">
              {t("consentManager.alwaysActive")}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center text-xs text-gray-300">
              <input
                type="checkbox"
                checked={checkBoxes.analytics}
                onChange={(e) =>
                  setCheckBoxes((prev) => ({
                    ...prev,
                    analytics: e.target.checked,
                  }))
                }
                id="analytics"
                className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("consentManager.categories.analytics")}
            </label>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center text-xs text-gray-300">
              <input
                type="checkbox"
                checked={checkBoxes.preferences}
                onChange={(e) =>
                  setCheckBoxes((prev) => ({
                    ...prev,
                    preferences: e.target.checked,
                  }))
                }
                id="preferences"
                className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("consentManager.categories.preferences")}
            </label>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center text-xs text-gray-300">
              <input
                type="checkbox"
                checked={checkBoxes.marketing}
                onChange={(e) =>
                  setCheckBoxes((prev) => ({
                    ...prev,
                    marketing: e.target.checked,
                  }))
                }
                id="marketing"
                className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {t("consentManager.categories.marketing")}
            </label>
          </div>
          <div className="pt-2">
            <CustomButton
              variant="primary"
              size="md"
              className="w-full text-xs"
              onClick={() => handleConsents("selection", checkBoxes)}
              title={t("consentManager.savePreferences")}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsentManager;
